import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { LineageGraph, LineageNode } from '../types/paper';
import { MathView } from './MathView';
import { Sparkles, GitBranch, ArrowRight, Info, ExternalLink } from 'lucide-react';

interface ConceptGraphProps {
  graph: LineageGraph;
  paperTitle: string;
}

export const ConceptGraph: React.FC<ConceptGraphProps> = ({ graph, paperTitle }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedNode, setSelectedNode] = useState<LineageNode | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'ancestor' | 'target_paper' | 'descendant'>('all');

  useEffect(() => {
    if (!svgRef.current || !graph.nodes.length) return;

    const width = svgRef.current.clientWidth || 900;
    const height = 550;

    // Filter nodes if needed
    const activeNodes = filterType === 'all' 
      ? graph.nodes 
      : graph.nodes.filter(n => n.type === filterType || n.type === 'target_paper');
    
    const activeNodeIds = new Set(activeNodes.map(n => n.id));
    const activeLinks = graph.links.filter(l => 
      activeNodeIds.has(typeof l.source === 'string' ? l.source : (l.source as any).id) &&
      activeNodeIds.has(typeof l.target === 'string' ? l.target : (l.target as any).id)
    );

    // Deep clone data for D3 simulation
    const nodesData = activeNodes.map(d => ({ ...d }));
    const linksData = activeLinks.map(d => ({ ...d }));

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const container = svg.append('g').attr('class', 'graph-container');

    // Zoom setup
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.4, 2.5])
      .on('zoom', (event) => {
        container.attr('transform', event.transform);
      });

    svg.call(zoom as any);

    // Arrow markers
    const defs = svg.append('defs');
    defs.append('marker')
      .attr('id', 'arrow-ancestor')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 28)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#a78bfa');

    defs.append('marker')
      .attr('id', 'arrow-descendant')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 28)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#34d399');

    // Force Simulation
    const simulation = d3.forceSimulation<any>(nodesData)
      .force('link', d3.forceLink(linksData).id((d: any) => d.id).distance(140))
      .force('charge', d3.forceManyBody().strength(-450))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(50));

    // Links
    const link = container.append('g')
      .selectAll('line')
      .data(linksData)
      .enter()
      .append('line')
      .attr('stroke', (d: any) => {
        return '#334155';
      })
      .attr('stroke-opacity', 0.8)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '4,4')
      .attr('marker-end', (d: any) => {
        const targetNode = nodesData.find(n => n.id === (typeof d.target === 'object' ? d.target.id : d.target));
        return targetNode?.type === 'descendant' ? 'url(#arrow-descendant)' : 'url(#arrow-ancestor)';
      });

    // Link Labels
    const linkLabel = container.append('g')
      .selectAll('text')
      .data(linksData)
      .enter()
      .append('text')
      .text((d: any) => d.label)
      .attr('font-size', '10px')
      .attr('fill', '#94a3b8')
      .attr('text-anchor', 'middle')
      .attr('dy', -5);

    // Nodes
    const node = container.append('g')
      .selectAll('.node-group')
      .data(nodesData)
      .enter()
      .append('g')
      .attr('class', 'node-group cursor-pointer')
      .call(d3.drag<any, any>()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended)
      )
      .on('click', (event, d) => {
        setSelectedNode(d as LineageNode);
      });

    // Node Circles
    node.append('circle')
      .attr('r', (d: any) => (d.type === 'target_paper' ? 26 : 20))
      .attr('fill', (d: any) => {
        if (d.type === 'target_paper') return '#2563eb';
        if (d.type === 'ancestor') return '#6d28d9';
        return '#059669';
      })
      .attr('stroke', (d: any) => {
        if (d.type === 'target_paper') return '#60a5fa';
        if (d.type === 'ancestor') return '#c084fc';
        return '#34d399';
      })
      .attr('stroke-width', (d: any) => (d.type === 'target_paper' ? 4 : 2.5))
      .attr('class', 'transition-all duration-300 hover:scale-110');

    // Node Year Label Badge
    node.append('text')
      .text((d: any) => d.year)
      .attr('text-anchor', 'middle')
      .attr('dy', (d: any) => (d.type === 'target_paper' ? -34 : -28))
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .attr('fill', (d: any) => (d.type === 'target_paper' ? '#60a5fa' : '#cbd5e1'));

    // Node Title Label
    node.append('text')
      .text((d: any) => d.label.length > 20 ? d.label.substring(0, 18) + '...' : d.label)
      .attr('text-anchor', 'middle')
      .attr('dy', (d: any) => (d.type === 'target_paper' ? 42 : 36))
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .attr('fill', '#f8fafc');

    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      linkLabel
        .attr('x', (d: any) => (d.source.x + d.target.x) / 2)
        .attr('y', (d: any) => (d.source.y + d.target.y) / 2);

      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    function dragstarted(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event: any, d: any) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }
  }, [graph, filterType]);

  return (
    <div className="relative w-full glass-panel rounded-2xl p-6 border border-slate-800 shadow-2xl overflow-hidden">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-4 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2 text-brand-glow text-sm font-semibold uppercase tracking-wider">
            <GitBranch className="w-4 h-4" />
            Lineage & Ancestry Graph
          </div>
          <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
            Concept Lineage for: <span className="text-brand-400">{paperTitle}</span>
          </h2>
        </div>

        {/* Filter Badges */}
        <div className="flex items-center gap-2 bg-dark-bg/80 p-1.5 rounded-xl border border-slate-800">
          {(['all', 'ancestor', 'target_paper', 'descendant'] as const).map(type => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                filterType === type
                  ? 'bg-brand-600 text-white shadow-glow-blue'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              {type === 'all' && 'All Nodes'}
              {type === 'ancestor' && '💜 Predecessors'}
              {type === 'target_paper' && '💙 Core Paper'}
              {type === 'descendant' && '💚 Descendants'}
            </button>
          ))}
        </div>
      </div>

      {/* SVG Canvas */}
      <div className="relative w-full h-[550px] bg-dark-bg/60 rounded-xl overflow-hidden border border-slate-800/80">
        <svg ref={svgRef} className="w-full h-full cursor-grab active:cursor-grabbing" />

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-dark-card/90 backdrop-blur-md p-3 rounded-xl border border-slate-800 text-xs flex flex-col gap-2 shadow-lg">
          <div className="font-semibold text-slate-300">Lineage Map Legend</div>
          <div className="flex items-center gap-2 text-purple-300">
            <div className="w-3 h-3 rounded-full bg-purple-600 border border-purple-400" />
            Ancestral / Cited Paper ($O(N)$ Recurrence, Additive Attention)
          </div>
          <div className="flex items-center gap-2 text-blue-300">
            <div className="w-3 h-3 rounded-full bg-blue-600 border border-blue-400 shadow-glow-blue" />
            Current Paper Innovation (Scaled Self-Attention)
          </div>
          <div className="flex items-center gap-2 text-emerald-300">
            <div className="w-3 h-3 rounded-full bg-emerald-600 border border-emerald-400" />
            Descendant / Successor Work (FlashAttention, RoPE, GQA)
          </div>
        </div>

        {/* Selected Node Drawer / Card */}
        {selectedNode && (
          <div className="absolute top-4 right-4 max-w-md w-full bg-dark-card/95 backdrop-blur-xl border border-slate-700/80 p-5 rounded-2xl shadow-2xl animate-fade-in text-slate-200 z-20">
            <div className="flex justify-between items-start mb-3">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                selectedNode.type === 'target_paper'
                  ? 'bg-blue-900/60 text-blue-300 border border-blue-500/40'
                  : selectedNode.type === 'ancestor'
                  ? 'bg-purple-900/60 text-purple-300 border border-purple-500/40'
                  : 'bg-emerald-900/60 text-emerald-300 border border-emerald-500/40'
              }`}>
                {selectedNode.type.toUpperCase()} • {selectedNode.year}
              </span>
              <button 
                onClick={() => setSelectedNode(null)} 
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800"
              >
                ✕
              </button>
            </div>

            <h3 className="text-lg font-bold text-white mb-1">{selectedNode.label}</h3>
            {selectedNode.authors && (
              <div className="text-xs text-slate-400 mb-3 font-mono">{selectedNode.authors}</div>
            )}

            <div className="text-sm text-slate-300 mb-4 leading-relaxed">
              {selectedNode.description}
            </div>

            <div className="p-3 bg-dark-bg/80 rounded-xl border border-slate-800">
              <div className="text-xs font-semibold text-brand-glow uppercase tracking-wider mb-1 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Key Innovation
              </div>
              <div className="text-xs text-slate-200 font-medium">
                {selectedNode.keyInnovation}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
